import { Colors } from '@amelio/ui-library';

import { BarChartTooltipOverlay } from '../BarChartTooltipOverlay/BarChartTooltipOverlay';
import { PlainTextResult } from '../PlainTextResult/PlainTextResult';
import {
    calculateMaxYValue,
    generateQuarterYAxisValues,
    getResponseLabel,
    orderOptionsWithOtherLast,
} from '../RankingQuestionResult/utils/chartCalculations';

import type { BarChartValue } from '../BarChart/BarChart';
import type { TextAnswer } from '@/components/QuestionResultContainer/types';
import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { getLocalizedText } from '@/utils/localization';

export interface CheckboxResultProps {
    question: ReviewQuestion;
    answerData: Record<string | number, number>;
    answeredCount?: number;
    textAnswers?: TextAnswer[];
    dataTestId?: string;
    className?: string;
    reviewee?: User;
}

export const CheckboxResult = ({
    question,
    answerData,
    answeredCount = 0,
    textAnswers,
    dataTestId = 'checkbox-result',
    className = '',
    reviewee,
}: CheckboxResultProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();

    const options = question.reviewQuestionOptions ?? [];

    const orderedOptions = orderOptionsWithOtherLast({
        options,
        hasOtherOption: question.hasOtherOption ?? false,
    });

    const values: BarChartValue[] = orderedOptions.map((option) => {
        const count = answerData[option.id ?? ''] ?? 0;

        return {
            label: replaceDynamicTags({ text: getLocalizedText(option.label), employee: reviewee }),
            value: count,
            badgeColor: Colors.PRIMARY,
            badgeTextColor: Colors.WHITE,
        };
    });

    const maxYValue = calculateMaxYValue(answeredCount, Object.values(answerData));
    const yValues = generateQuarterYAxisValues(maxYValue);

    const renderTooltipContent = (barValue: BarChartValue) => {
        const count = barValue.value;
        const label = getResponseLabel({
            count,
            singularLabel: t('common.vote'),
            pluralLabel: t('common.votes'),
        });

        return `${count} ${label}`;
    };

    return (
        <div className={className}>
            <BarChartTooltipOverlay
                values={values}
                yValues={yValues}
                maxYValue={maxYValue}
                className={'mt-4'}
                dataTestId={`${dataTestId}-bar-chart`}
                renderTooltipContent={renderTooltipContent}
            />

            {textAnswers && textAnswers.length > 0 && (
                <PlainTextResult
                    answers={textAnswers}
                    className={'mt-6'}
                    dataTestId={`${dataTestId}-text-answers`}
                />
            )}
        </div>
    );
};

export default CheckboxResult;
